// Shared settings-nav definition — the single source of truth for the
// settings sections, their grouping, icons, and plan gating. Consumed by
// TeamSettingsPage.vue (tab slugs + slug validation) and Sidebar.vue (the
// main sidebar's settings mode), so the two surfaces can't drift apart.
import type { Component } from 'vue'
import {
  PhArrowLeft,
  PhArrowUUpRight,
  PhBuildings,
  PhChartBar,
  PhChatCircleDots,
  PhCheckCircle,
  PhCode,
  PhGauge,
  PhGear,
  PhGlobe,
  PhListChecks,
  PhPalette,
  PhPulse,
  PhReceipt,
  PhSlidersHorizontal,
  PhStack,
} from '@phosphor-icons/vue'
import type { FeatureFlag } from '@/types'

export type SettingsNavAccess = 'full' | 'limited' | 'gated' | 'hidden'

export interface SettingsNavItemDef {
  /** Tab slug — also the URL segment (/settings/:id). 'general' lives at the bare /settings URL. */
  id: string
  labelKey: string
  labelFallback: string
  icon: Component
  /** In-page header title where it differs from the menu label (e.g. General →
   * "General Settings"). Menu labels stay short; the page title spells it out. */
  pageTitleKey?: string
  pageTitleFallback?: string
  /** Plan gate — resolved to an access level by resolveSettingsNavGroups. */
  requiredFeature?: FeatureFlag
}

export interface SettingsNavGroupDef {
  labelKey: string
  labelFallback: string
  items: SettingsNavItemDef[]
}

export interface ResolvedSettingsNavItem extends SettingsNavItemDef {
  /** 'full'/'limited' = usable, 'gated' = show locked/upgrade, 'hidden' = not on this plan at all. */
  access: SettingsNavAccess
}

export interface ResolvedSettingsNavGroup {
  labelKey: string
  labelFallback: string
  items: ResolvedSettingsNavItem[]
}

/** Icon for the "back to the main menu" row in the sidebar's settings mode. */
export const SETTINGS_BACK_ICON = PhArrowLeft

export const SETTINGS_NAV_GROUPS: SettingsNavGroupDef[] = [
  {
    labelKey: 'settings.group.workspace',
    labelFallback: 'Workspace',
    items: [
      { id: 'general', labelKey: 'settings.general', labelFallback: 'General', icon: PhGear, pageTitleKey: 'settings.pageTitle.general', pageTitleFallback: 'General Settings' },
      { id: 'company', labelKey: 'settings.company', labelFallback: 'Company', icon: PhBuildings, pageTitleKey: 'settings.pageTitle.company', pageTitleFallback: 'Company Settings' },
      { id: 'content-approvals', labelKey: 'settings.contentApproval.title', labelFallback: 'Content approvals', icon: PhCheckCircle },
    ],
  },
  {
    labelKey: 'settings.group.brandSite',
    labelFallback: 'Brand & site',
    items: [
      { id: 'defaults', labelKey: 'settings.siteDefaults', labelFallback: 'Defaults', icon: PhSlidersHorizontal, pageTitleKey: 'settings.pageTitle.defaults', pageTitleFallback: 'Default Settings' },
      { id: 'branding', labelKey: 'settings.branding', labelFallback: 'Branding', icon: PhPalette },
      { id: 'localization', labelKey: 'settings.websiteLanguages.tab', labelFallback: 'Localization', icon: PhGlobe },
      { id: 'analytics', labelKey: 'settings.analytics', labelFallback: 'Analytics', icon: PhChartBar },
      // Kits tab — AI-generated section kits. This is the site-level look
      // every new page inherits (stored as Workspace.themeKey).
      { id: 'kits', labelKey: 'settings.kits.tab', labelFallback: 'Kits', icon: PhStack },
      { id: 'domains', labelKey: 'settings.domains', labelFallback: 'Domains', icon: PhGlobe },
      { id: 'redirects', labelKey: 'settings.redirects', labelFallback: 'Redirects', icon: PhArrowUUpRight, requiredFeature: 'settings.redirects' },
      // URL slug 'code'; legacy 'scripts'/'custom-code' remapped in TeamSettingsPage's alias map.
      { id: 'code', labelKey: 'settings.customCode', labelFallback: 'Custom Code', icon: PhCode, requiredFeature: 'settings.customcode' },
    ],
  },
  {
    labelKey: 'settings.group.communication',
    labelFallback: 'Communication',
    items: [
      // Website chat — the chat bubble on the merchant's website (show it,
      // greeting, colour, ask for email). Its messages land in the Inbox.
      { id: 'website-chat', requiredFeature: 'inbox', labelKey: 'settings.websiteChat.tab', labelFallback: 'Website chat', icon: PhChatCircleDots, pageTitleKey: 'settings.pageTitle.websiteChat', pageTitleFallback: 'Website Chat Settings' },
      // Lists — marketing email lists as opt-in config (define lists, consent
      // rules, resend confirmations). The PEOPLE on a list live in Leads /
      // Customers behind a list filter, not here. Moved from the main nav's
      // Activity group 2026-07-21.
      { id: 'lists', labelKey: 'settings.lists.tab', labelFallback: 'Lists', icon: PhListChecks, requiredFeature: 'customers' },
      // Lead score — the points rules and the line that moves a person to
      // Ready for sales (backend services/crm/score.js).
      { id: 'lead-score', labelKey: 'settings.leadScore.tab', labelFallback: 'Lead score', icon: PhGauge, requiredFeature: 'customers' },
      // Sending mailboxes and their warm-up moved to Prospecting › Mailboxes (2026-10-29).
      { id: 'order-confirmation', labelKey: 'settings.orderConfirmation.tab', labelFallback: 'Order Confirmation', icon: PhReceipt },
      { id: 'custom-events', labelKey: 'settings.customEvents.tab', labelFallback: 'Custom Events', icon: PhPulse },
    ],
  },
]

/**
 * Resolve the settings nav for one workspace: stamps each item's plan access
 * level. How a gated/hidden item is presented is the consumer's call (disabled
 * tab on the settings page, lock + upgrade modal in the sidebar).
 */
export function resolveSettingsNavGroups(opts: {
  getFeatureAccess: (flag: FeatureFlag) => SettingsNavAccess
}): ResolvedSettingsNavGroup[] {
  return SETTINGS_NAV_GROUPS.map(group => ({
    labelKey: group.labelKey,
    labelFallback: group.labelFallback,
    items: group.items.map(item => ({
      ...item,
      access: item.requiredFeature ? opts.getFeatureAccess(item.requiredFeature) : 'full',
    })),
  }))
}

/** Workspace-relative route for a settings item — General lives at the bare /settings URL. */
export function settingsItemRoute(id: string): string {
  return id === 'general' ? '/settings' : `/settings/${id}`
}

/** Flat item lookup by tab slug. */
export function findSettingsNavItem(id: string): SettingsNavItemDef | null {
  for (const group of SETTINGS_NAV_GROUPS) {
    const item = group.items.find(i => i.id === id)
    if (item) return item
  }
  return null
}
